import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FlexBox, Size } from '@lumapps/lumx/react';

import { AiDetailedFeedback, AiFeedbackTypes } from '../../types';
import { AiFeedbackReasons } from '../AiFeedbackReason';
import { AiReactions } from '../AiReactions';

const CLASSNAME = 'ai-feedback-and-reactions';

export interface AiFeedbackAndReactionsProps {
    /** Current feedback type (like, dislike, or undefined) */
    feedbackType?: AiFeedbackTypes;
    /** Whether the feedback reasons panel is currently open */
    isFeedbackReasonsOpen: boolean;
    /** Handler for when a feedback button (like/dislike) is clicked */
    onFeedbackClick: (feedbackType: AiFeedbackTypes) => void;
    /** Handler for closing the feedback reasons panel */
    onCloseFeedbackReasons: () => void;
    /** Handler for sending detailed feedback with reason and comment */
    sendFeedback: (feedback: AiDetailedFeedback) => void;
    /** Content to copy to clipboard */
    contentToCopy?: string;
    /** Icon sizes for reactions */
    size?: 's' | 'm';
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** Whether to disable buttons (e.g., while loading) */
    isDisabled?: boolean;
    /** Whether to disable buttons after feedback is given */
    disableAfterFeedback?: boolean;
    /** Custom content to render after the reactions (e.g., agent picker) */
    afterReaction?: React.ReactNode;
    /** Whether to show the feedback reasons panel */
    enableFeedbackReasons?: boolean;
    /** Whether to show the feedback reason options (chip selector) in the feedback panel */
    enableReasonOptions?: boolean;
    /** Whether to show the comment field in the feedback panel */
    enableCommentField?: boolean;
    /** Maximum character length for the comment field */
    maxCommentLength?: number;
    /** Custom CSS class */
    className?: string;
    /** Custom scope for data attributes */
    scope?: string;
    /** Ref for the dislike button, forwarded to AiReactions for focus restoration */
    dislikeButtonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * Combined component for AI feedback and reactions.
 *
 * Integrates AiReactions (like/dislike/copy buttons) and AiFeedbackReasons (detailed feedback form).
 * Fully controlled component - use the useAiFeedback hook for state management or manage state yourself.
 */
export const AiFeedbackAndReactions: React.FC<AiFeedbackAndReactionsProps> = ({
    feedbackType,
    isFeedbackReasonsOpen,
    onFeedbackClick,
    onCloseFeedbackReasons,
    sendFeedback,
    contentToCopy,
    size = 's',
    isPreview = false,
    isDisabled = false,
    disableAfterFeedback = true,
    afterReaction,
    enableFeedbackReasons = true,
    enableReasonOptions = true,
    enableCommentField = true,
    maxCommentLength,
    className,
    scope,
    dislikeButtonRef,
}) => {
    const { block } = useClassnames(CLASSNAME);
    const copyButtonRef = React.useRef<HTMLButtonElement>(null);

    return (
        <FlexBox className={block({}, className)} orientation="vertical" gap={Size.regular}>
            <FlexBox orientation="horizontal" gap={Size.big} hAlign="center">
                <AiReactions
                    size={size}
                    contentToCopy={contentToCopy}
                    isPreview={isPreview}
                    copyButtonRef={copyButtonRef}
                    dislikeButtonRef={dislikeButtonRef}
                    feedbackType={feedbackType}
                    onFeedbackClick={onFeedbackClick}
                    disableAfterFeedback={disableAfterFeedback}
                    isDisabled={isDisabled}
                    scope={scope}
                />

                {afterReaction}
            </FlexBox>

            {enableFeedbackReasons && (
                <AiFeedbackReasons
                    isOpen={isFeedbackReasonsOpen && !isDisabled}
                    onClose={onCloseFeedbackReasons}
                    sendFeedback={sendFeedback}
                    feedbackType={feedbackType}
                    showReasonOptions={enableReasonOptions}
                    showCommentField={enableCommentField}
                    maxCommentLength={maxCommentLength}
                />
            )}
        </FlexBox>
    );
};
